import { motion } from 'framer-motion';

const benefitsList = [
  'Same-day prescription filling with insurance billing',
  'Free medication delivery to your door',
  'Comprehensive medication reviews & drug interaction checks',
  'Blister packaging & compliance monitoring',
  'MAT – addiction treatment medications for prescription & licit drug abuse',
  'Minor ailment prescribing for 40+ conditions without a doctor visit',
  'Over-the-counter medication access with pharmacist guidance',
  'Smoking cessation programs with Zonnic pouches',
];

const featureCards = [
  {
    icon: 'ri-capsule-line',
    title: 'Prescription Dispensing',
    description: 'Fast, accurate dispensing with generic alternatives and direct insurance billing.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'MAT – Medication Assisted Treatment',
    description: 'Addiction treatment medications for prescription & licit drug abuse, dispensed with care and confidentiality.',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Over-The-Counter Medication',
    description: 'Wide selection of OTC products with expert pharmacist guidance to find the right solution for you.',
  },
  {
    icon: 'ri-stethoscope-line',
    title: 'Minor Ailment Prescribing',
    description: 'Get a prescription for 40+ common conditions directly from our pharmacist — no doctor visit needed.',
  },
];

export default function PharmacyBenefits() {
  return (
    <section aria-labelledby="benefits-heading" className="mb-20">
      {/* Benefits block */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-14">
        {/* Text side */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, ease: 'easeOut' }}
          className="order-2 lg:order-1"
        >
          <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-4">What We Offer</p>
          <h2 id="benefits-heading" className="text-3xl font-bold text-foreground-950 mb-4 leading-snug">
            Pharmacy services designed around you
          </h2>
          <p className="text-foreground-500 leading-relaxed text-[0.94rem] mb-8">
            Our team provides <strong className="text-foreground-700 font-semibold">pharmacy services</strong> that
            go beyond dispensing — offering comprehensive health solutions that keep you informed, safe, and
            on track with your treatment plan.
          </p>
          <ul role="list" className="space-y-3">
            {benefitsList.map((item, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.065, ease: 'easeOut' }}
                className="flex items-start gap-3 text-gray-600 text-sm"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0 mt-[7px]" aria-hidden="true" />
                {item}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Image side */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="order-1 lg:order-2 rounded-lg overflow-hidden w-full h-72 lg:h-[420px]"
        >
          <img
            src="https://readdy.ai/api/search-image?query=pharmacist%20handing%20medication%20to%20patient%20organized%20bright%20pharmacy%20shelves%20background%20professional%20teal%20white%20clean%20environment%20modern%20canadian%20pharmacy%20interior%20warm%20lighting%20close%20up%20interaction&width=700&height=500&seq=phbenefit-img&orientation=landscape"
            alt="Pharmacy benefits at Thornhill Drug-Mart"
            className="w-full h-full object-cover object-top"
            width={700}
            height={500}
            loading="lazy"
            decoding="async"
          />
        </motion.div>
      </div>

      {/* Feature cards — left border accent style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {featureCards.map((card, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.09, ease: 'easeOut' }}
            className="flex gap-5 p-6 bg-background-50 border-l-2 border-l-primary-500 border border-background-200 rounded-r-lg"
          >
            <div className="w-10 h-10 flex items-center justify-center text-primary-600 flex-shrink-0 mt-0.5" aria-hidden="true">
              <i className={`${card.icon} text-2xl`}></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">{card.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{card.description}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
